import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Lock, ArrowLeft, CheckCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [mode, setMode] = useState(token ? 'reset' : 'request'); // 'request' or 'reset'
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [tokenValid, setTokenValid] = useState(null);
  const [tokenChecking, setTokenChecking] = useState(!!token);

  // Verify token on mount if present
  useEffect(() => {
    if (token) {
      verifyToken();
    }
  }, [token]);

  const verifyToken = async () => {
    try {
      const response = await axios.get(`${API}/auth/verify-reset-token?token=${token}`);
      setTokenValid(response.data.valid);
    } catch (error) {
      setTokenValid(false);
    } finally {
      setTokenChecking(false);
    }
  };

  const handleRequestReset = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Vul je e-mailadres in');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: email.toLowerCase() });
      setSuccess(true);
      toast.success('E-mail verzonden!');
    } catch (error) {
      toast.error('Er ging iets mis. Probeer het later opnieuw.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (newPassword.length < 6) {
      toast.error('Wachtwoord moet minimaal 6 tekens bevatten');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error('Wachtwoorden komen niet overeen');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: newPassword
      });
      setSuccess(true);
      toast.success('Wachtwoord gewijzigd!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Er ging iets mis');
    } finally {
      setLoading(false);
    }
  };

  // Loading state while checking token
  if (tokenChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" data-testid="reset-password-loading">
        <div className="w-8 h-8 border-4 border-gold/30 border-t-gold rounded-full animate-spin" />
      </div>
    );
  }

  // Invalid token state
  if (mode === 'reset' && tokenValid === false) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 py-12" data-testid="reset-password-invalid">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <Card className="border-2 border-border shadow-xl">
            <CardContent className="pt-8 pb-8 text-center">
              <AlertCircle className="w-16 h-16 text-destructive mx-auto mb-4" />
              <h2 className="font-display text-2xl text-foreground mb-2">
                Ongeldige link
              </h2>
              <p className="text-muted-foreground mb-6">
                Deze reset link is ongeldig of verlopen. Vraag een nieuwe aan.
              </p>
              <Button
                onClick={() => {
                  setMode('request');
                  setTokenValid(null);
                }}
                className="rounded-full bg-gold text-night-blue hover:bg-gold/90"
                data-testid="request-new-link-btn"
              >
                Nieuwe link aanvragen
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 py-12" data-testid="reset-password-success">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <Card className="border-2 border-border shadow-xl">
            <CardContent className="pt-8 pb-8 text-center">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h2 className="font-display text-2xl text-foreground mb-2">
                {mode === 'request' ? 'E-mail verzonden!' : 'Wachtwoord gewijzigd!'}
              </h2>
              <p className="text-muted-foreground mb-6">
                {mode === 'request' 
                  ? 'Controleer je inbox (en spam folder) voor de reset link.'
                  : 'Je kunt nu inloggen met je nieuwe wachtwoord.'}
              </p>
              <Button
                onClick={() => navigate('/login')}
                className="rounded-full bg-gold text-night-blue hover:bg-gold/90"
                data-testid="back-to-login-btn"
              >
                Naar inloggen
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12" data-testid="reset-password-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <Card className="border-2 border-border shadow-xl">
          <CardHeader className="text-center pb-2">
            <div className="w-16 h-16 rounded-full bg-gold/20 flex items-center justify-center mx-auto mb-4">
              {mode === 'request' ? (
                <Mail className="w-8 h-8 text-gold" />
              ) : (
                <Lock className="w-8 h-8 text-gold" />
              )}
            </div>
            <CardTitle className="font-display text-2xl">
              {mode === 'request' ? 'Wachtwoord vergeten?' : 'Nieuw wachtwoord'}
            </CardTitle>
            <CardDescription className="font-accent">
              {mode === 'request' 
                ? 'Geen probleem! Vul je e-mailadres in en we sturen je een reset link.'
                : 'Kies een nieuw wachtwoord voor je account.'}
            </CardDescription>
          </CardHeader>
          
          <CardContent className="pt-4">
            {mode === 'request' ? (
              <form onSubmit={handleRequestReset} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="font-accent">E-mailadres</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="email"
                      data-testid="reset-email-input"
                      type="email"
                      placeholder="oma@verhaaltjes.nl"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="pl-10"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  data-testid="send-reset-btn"
                  disabled={loading}
                  className="w-full rounded-full bg-old-rose hover:bg-old-rose/80 text-night-blue font-bold"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-night-blue/30 border-t-night-blue rounded-full animate-spin" />
                  ) : (
                    'Verstuur reset link'
                  )}
                </Button>
              </form>
            ) : (
              <form onSubmit={handleResetPassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="new-password" className="font-accent">Nieuw wachtwoord</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="new-password"
                      data-testid="new-password-input"
                      type="password"
                      placeholder="Minimaal 6 tekens"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      minLength={6}
                      className="pl-10"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirm-password" className="font-accent">Bevestig wachtwoord</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="confirm-password"
                      data-testid="confirm-password-input"
                      type="password"
                      placeholder="Herhaal je wachtwoord"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      className="pl-10"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  data-testid="reset-password-btn"
                  disabled={loading}
                  className="w-full rounded-full bg-old-rose hover:bg-old-rose/80 text-night-blue font-bold"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-night-blue/30 border-t-night-blue rounded-full animate-spin" />
                  ) : (
                    'Wachtwoord wijzigen'
                  )}
                </Button>
              </form>
            )}

            <div className="mt-6 text-center">
              <Button
                variant="link"
                onClick={() => navigate('/login')}
                className="text-muted-foreground hover:text-foreground"
                data-testid="back-to-login-link"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Terug naar inloggen
              </Button>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default ResetPassword;
